import { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { Layer, Marker, NavigationControl, Map as ReactMapGL, Source } from 'react-map-gl/mapbox';
import { ButtonUI, CustomThemeProvider, ModalUI, usePortal } from 'sonata-react-components';
import { useTheme } from 'styled-components';

import { getGoogleMapsWalkingRouteUrl } from '@/components/doohReport/common/helpers/googleMaps.helper';
import useWalkingRoute from './useWalkingRoute';

import type { LayerProps } from 'react-map-gl/mapbox';
import type { DoohReportLocation } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

import './routeModal.scss';

type ModalHandler = { closeModal: () => void; openModal: () => void };

type Props = {
  id: string;
  title: string;
  origin: DoohReportLocation;
  destination: DoohReportLocation;
  originIcon?: string;
  destinationIcon?: string;
  isOpen: boolean;
  onClose: () => void;
};

const ROUTE_SOURCE_ID = 'routeModal-route-source';

const ROUTE_LAYER: LayerProps = {
  id: 'routeModal-route-line',
  layout: { 'line-cap': 'round', 'line-join': 'round' },
  paint: { 'line-color': '#2f6feb', 'line-width': 5 },
  source: ROUTE_SOURCE_ID,
  type: 'line'
};

export default function RouteModal({
  id,
  title,
  origin,
  destination,
  originIcon = 'fa-kit fa-screen',
  destinationIcon = 'fa-solid fa-location-dot',
  isOpen,
  onClose
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { themeContrast } = useTheme() as { themeContrast: 'light' | 'dark' | 'darkAI' };
  const modalRef = useRef<ModalHandler>(null);
  const route = useWalkingRoute(origin, destination, isOpen);

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.openModal();
    } else {
      modalRef.current?.closeModal();
    }
  }, [isOpen]);

  const routeGeoJson = useMemo<GeoJSON.Feature<GeoJSON.LineString> | null>(
    () =>
      route
        ? { geometry: { coordinates: route.coordinates, type: 'LineString' }, properties: {}, type: 'Feature' }
        : null,
    [route]
  );

  const modal = ReactDOM.createPortal(
    <CustomThemeProvider themeContrast={themeContrast}>
      <ModalUI className="routeModal" closeModalCallback={onClose} id={id} ref={modalRef} title={title} width="760px">
        <div className="routeModal__map">
          <ReactMapGL
            attributionControl={false}
            id={`${id}-map`}
            initialViewState={{
              latitude: (origin.lat + destination.lat) / 2,
              longitude: (origin.lng + destination.lng) / 2,
              zoom: 14
            }}
            mapStyle="mapbox://styles/mapbox/streets-v12"
            mapboxAccessToken={taptap.config.mapbox.accessToken}
            maxZoom={18}
            minZoom={2}
          >
            <NavigationControl position="bottom-right" showCompass={false} visualizePitch={false} />
            {routeGeoJson && (
              <Source data={routeGeoJson} id={ROUTE_SOURCE_ID} type="geojson">
                <Layer {...ROUTE_LAYER} />
              </Source>
            )}
            <Marker latitude={origin.lat} longitude={origin.lng}>
              <i className={`${originIcon} routeModal__marker routeModal__marker--origin`} />
            </Marker>
            <Marker latitude={destination.lat} longitude={destination.lng}>
              <i className={`${destinationIcon} routeModal__marker routeModal__marker--destination`} />
            </Marker>
          </ReactMapGL>
        </div>
        <div className="routeModal__footer">
          <ButtonUI
            id={`${id}-closeButton`}
            onClick={onClose}
            text={formatMessage({ defaultMessage: 'Close', id: 'doohReport.routeModal.close' })}
            type="secondary"
          />
          <ButtonUI
            icon="fa-solid fa-arrow-up-right-from-square"
            iconPosition="right"
            id={`${id}-viewOnMapButton`}
            onClick={() =>
              window.open(getGoogleMapsWalkingRouteUrl(origin, destination), '_blank', 'noopener,noreferrer')
            }
            text={formatMessage({ defaultMessage: 'View on map', id: 'doohReport.routeModal.viewOnMap' })}
            type="primary"
          />
        </div>
      </ModalUI>
    </CustomThemeProvider>,
    usePortal(id)
  );

  return isOpen ? modal : null;
}
